import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, Check, X, Clock, ShoppingBag, PiggyBank, AlertTriangle, Package, Pencil, Trash2, Sparkles, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";
import InspectListingModal, { inspectVariantForOrder, fulfillmentLabel } from "@/components/InspectListingModal";
// Iteration 277 — cash-out cards are shared verbatim with the main
// Dashboard moderation queue so both surfaces stay in lockstep.
import { CashoutCard } from "@/components/UnifiedPendingQueue";

/**
 * PendingApprovalsInbox — dashboard tile listing all pending kid purchases +
 * allowance requests from the household. Both types render as compact rows
 * with 1-tap [Approve] / [Reject] buttons that call the local Phase 2 stub
 * endpoints and refresh the tile in place.
 *
 * Auto-polls every 45s so a parent's dashboard stays fresh while a kid is
 * actively shopping in Kid Zone on another device.
 */
function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch { return ""; }
}

function timeRemaining(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = then - now;
    if (diff <= 0) return { expired: true, label: "Expired" };
    const hrs = Math.floor(diff / (60 * 60 * 1000));
    const mins = Math.round((diff % (60 * 60 * 1000)) / 60000);
    if (hrs > 0) return { expired: false, label: `${hrs}h ${mins}m left` };
    return { expired: false, label: `${mins}m left` };
  } catch { return null; }
}

export default function PendingApprovalsInbox({ kidsById, onToast, onEditListing, onDeleteListing, refreshTrigger = 0 }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [listings, setListings] = useState([]);
  // Iteration 277 — child cash-out requests surface in this inbox too.
  const [cashouts, setCashouts] = useState([]);
  const [cashoutPinId, setCashoutPinId] = useState("");
  const [cashoutPin, setCashoutPin] = useState("");
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  // Iteration 259 — view-only inspection modal state.
  // Shape: { variant, kind: "listing"|"order", id, payload, details }
  const [inspect, setInspect] = useState(null);
  // Iteration 75 — parent-side fulfillment picker state for bulky items.
  // Keyed by listing_id, value is a Set-like array of chosen options.
  const [bulkyChoices, setBulkyChoices] = useState({});
  const [savingBulkyId, setSavingBulkyId] = useState("");
  const flashTimerRef = React.useRef(null);

  const showFlash = useCallback((msg) => {
    setFlash(msg);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(""), 4500);
  }, []);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [{ data }, cashoutRes] = await Promise.all([
        api.get("/parent/pending-approvals"),
        // Iteration 277 — pending child cash-outs ride the same poll.
        api.get("/parent/cashout-requests?include_decided=false").catch(() => ({ data: { requests: [] } })),
      ]);
      setOrders(data?.pending_orders || []);
      setRequests(data?.allowance_requests || []);
      setListings(data?.pending_listings || []);
      setCashouts(cashoutRes?.data?.requests || []);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't load pending approvals. Please try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => {
      clearInterval(t);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [load]);

  // Iteration 207 — reload when parent signals an edit/delete happened
  // (bumps `refreshTrigger`) so the freshly-edited or freshly-deleted
  // listing disappears from the inbox without waiting for the 45s poll.
  useEffect(() => {
    if (refreshTrigger > 0) load();
  }, [refreshTrigger, load]);

  const kidNameFor = (kid_id) => kidsById?.[kid_id]?.name || kidsById?.[kid_id]?.kid_name || "Your kid";

  // Iteration 208 — bulky fulfillment picker uses the canonical modes.
  // Iteration 263 — Local Shop Pick-Up removed: permanently unavailable
  // platform-wide (Coming Soon).
  const BULKY_FULFILLMENT_OPTS = [
    { value: "parent_handoff",  label: "Parent Handoff" },
    { value: "school_gate",     label: "At/After School Collection" },
    { value: "home_delivery",   label: "Royal Mail Post (if postable)" },
  ];
  const toggleBulkyChoice = (listingId, value) => {
    setBulkyChoices((prev) => {
      const current = new Set(prev[listingId] || []);
      if (current.has(value)) current.delete(value); else current.add(value);
      return { ...prev, [listingId]: Array.from(current) };
    });
  };
  const saveBulkyChoices = async (listingId) => {
    const choices = bulkyChoices[listingId] || [];
    if (choices.length === 0) {
      onToast?.("Pick at least one fulfillment option first.", "error");
      return;
    }
    setSavingBulkyId(listingId);
    try {
      await api.post(`/parent/listings/${listingId}/fulfillment`, { choices });
      // Refresh so the card shows the choices persisted.
      await load();
      onToast?.("Fulfillment saved — you can now approve the sale.", "success");
    } catch (e) {
      onToast?.(formatApiError(e) || "Couldn't save fulfillment options.", "error");
    } finally {
      setSavingBulkyId("");
    }
  };

  // Iteration 277 — cash-out actions (identical contract to the main
  // Dashboard moderation queue; approve needs the parent's 4-digit PIN).
  const approveCashout = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/parent/cashout-requests/${r.id}/approve`, { pin: cashoutPin });
      setCashouts((rows) => rows.filter((x) => x.id !== r.id));
      setCashoutPinId(""); setCashoutPin("");
      setFlash(`Approved — ${formatGBP((r.amount_pennies || 0) / 100)} moved to your household balance.`);
      setTimeout(() => setFlash(""), 3500);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't approve the transfer — check your PIN and try again.");
    } finally {
      setBusyId("");
    }
  };

  const declineCashout = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/parent/cashout-requests/${r.id}/decline`);
      setCashouts((rows) => rows.filter((x) => x.id !== r.id));
      setFlash("Declined — the money stays in your child's wallet.");
      setTimeout(() => setFlash(""), 3500);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't decline the transfer.");
    } finally {
      setBusyId("");
    }
  };

  const decide = async (kind, id, approved) => {    setBusyId(id);
    try {
      // Unified upstream contract: POST /api/parent/approvals/{id}/action
      // with {action: "approve"|"reject"}. Legacy per-type endpoints still
      // work but the unified one is the source of truth going forward.
      await api.post(`/parent/approvals/${id}/action`, { action: approved ? "approve" : "reject" });
      setInspect(null);
      if (kind === "order") setOrders((rows) => rows.filter((r) => r.id !== id));
      else setRequests((rows) => rows.filter((r) => r.id !== id));
      const label = kind === "order"
        ? (approved ? "Approved — your kid can grab the toy!" : "Released — item is back on the marketplace.")
        : (approved ? "Acknowledged — allowance top-up is on you now." : "Dismissed the allowance ask.");
      showFlash(label);
      onToast?.(label, approved ? "success" : "info");
    } catch (e) {
      const msg = formatApiError(e) || "Could not save your decision — please try again.";
      showFlash(msg);
      onToast?.(msg, "error");
    } finally { setBusyId(""); }
  };

  // ----- Iteration 259 — view-only inspection modal -----
  const cleanHandle = (h) => String(h || "").replace(/^@+/, "");

  const openInspectListing = (l) => setInspect({
    variant: "new_listing",
    kind: "listing",
    id: l.listing_id,
    payload: l,
    details: {
      badge: "NEW LISTING",
      title: l.listing_title || "Listing",
      photos: (Array.isArray(l.listing_photos) && l.listing_photos.length
        ? l.listing_photos
        : (l.listing_thumbnail ? [l.listing_thumbnail] : [])),
      priceLabel: formatGBP(l.listing_price || 0),
      condition: l.listing_condition,
      description: l.listing_description,
      fulfillmentLabel: fulfillmentLabel(l.fulfillment_method),
      listedByLine: `${l.kid_alias || kidNameFor(l.kid_id)}${l.kid_handle ? ` (@${cleanHandle(l.kid_handle)})` : ""}`,
    },
  });

  const openInspectOrder = (o) => {
    const variant = inspectVariantForOrder(o);
    setInspect({
      variant,
      kind: "order",
      id: o.id,
      payload: o,
      details: {
        badge: variant === "piggy" ? "PIGGY BANK PURCHASE" : variant === "handover" ? "PARENT HAND-OVER" : "SCHOOL COLLECTION",
        title: o.item_title || o.listing_title || "Toy",
        photos: Array.isArray(o.photos) ? o.photos : [],
        priceLabel: formatGBP((o.price_pennies || o.amount_pennies || 0) / 100),
        condition: o.condition,
        description: o.item_description,
        fulfillmentLabel: fulfillmentLabel(o.fulfillment_method),
        buyerLine: `${o.buyer_name || kidNameFor(o.buyer_kid_id || o.kid_id)}${o.buyer_handle ? ` (@${cleanHandle(o.buyer_handle)})` : ""} wants to buy`,
        sellerLine: `${o.seller_name || "Seller"}${o.seller_handle ? ` (@${cleanHandle(o.seller_handle)})` : ""}`,
      },
    });
  };

  const approveListing = async (l) => {
    setBusyId(l.listing_id);
    try {
      await api.post(`/listings/${l.listing_id}/parent-approve`, { approve: true });
      setInspect(null);
      showFlash("Approved — the listing is live on the marketplace.");
      await load();
      onToast?.("Approved — it's live now!", "success");
    } catch (e) { setErr(formatApiError(e) || "Couldn't approve."); }
    finally { setBusyId(""); }
  };

  const rejectListing = async (l) => {
    setBusyId(l.listing_id);
    try {
      await api.post(`/listings/${l.listing_id}/parent-approve`, { approve: false });
      setInspect(null);
      showFlash("Rejected — the listing has been removed.");
      await load();
      onToast?.("Rejected.", "info");
    } catch (e) { setErr(formatApiError(e) || "Couldn't reject."); }
    finally { setBusyId(""); }
  };

  const totalPending = orders.length + requests.length + listings.length + cashouts.length;

  return (
    <div className="card-base p-6 md:p-8 mt-6" data-testid="pending-approvals-inbox">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandTeal text-white shadow-sm relative">
            <Inbox className="w-4 h-4" />
            {totalPending > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brandPink text-white text-[10px] font-bold inline-flex items-center justify-center border-2 border-white" data-testid="pending-approvals-count">
                {totalPending}
              </span>
            )}
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900">Pending Approvals</h2>
            <p className="text-xs text-slate-500 mt-0.5">Kid purchases &amp; allowance asks &mdash; one tap to decide.</p>
          </div>
        </div>
      </div>

      {flash && (
        <div
          className="mb-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-900 flex items-center gap-2 animate-in fade-in slide-in-from-top-1"
          data-testid="pending-approvals-flash"
          role="status"
        >
          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> <span className="min-w-0 break-words">{flash}</span>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="pending-approvals-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading&hellip;
        </div>
      ) : err ? (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid="pending-approvals-error">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      ) : totalPending === 0 ? (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-6 text-center" data-testid="pending-approvals-empty">
          <div className="font-bold text-emerald-900">All caught up!</div>
          <p className="text-xs text-emerald-800 mt-1">No pending kid purchases or allowance requests right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Iteration 277 — child cash-out requests (Transfer to Parent).
              Approve needs the parent's 4-digit PIN; decline unlocks the
              kid's funds. Same card as the main Dashboard queue. */}
          {cashouts.map((r) => (
            <CashoutCard
              key={`cashout-${r.id}`}
              request={r}
              busy={busyId === r.id}
              isPinPrompted={cashoutPinId === r.id}
              pin={cashoutPin}
              onPinChange={setCashoutPin}
              onApproveStart={() => { setCashoutPinId(r.id); setCashoutPin(""); }}
              onApproveCancel={() => { setCashoutPinId(""); setCashoutPin(""); }}
              onApproveConfirm={() => approveCashout(r)}
              onDecline={() => declineCashout(r)}
            />
          ))}
          {/* Iteration 91 — kid-edit re-approvals surface here alongside
              purchase & allowance approvals. Clicking Re-approve pushes
              the pending_edit upstream via the same endpoint the queue
              CTA uses. */}
          {listings.map((l) => {
            const busy = busyId === l.listing_id;
            const priceLabel = formatGBP(l.listing_price || 0);
            const kidName = l.kid_alias || kidNameFor(l.kid_id);
            const pe = l.pending_edit || {};
            // Iteration 206 — differentiate first-time kid submissions
            // (no `pending_edit`) from kid-edit re-approvals (has
            // `pending_edit`). First-time submissions render an
            // "Approve" button that hits the canonical
            // `POST /api/listings/{id}/parent-approve` route.
            const isFirstApproval = (l.kind === "listing_first_approval") || !l.pending_edit;
            // Iteration 95 — compute a Was → Now diff across the four
            // seller-editable fields the kid can change. Only shown when
            // the pending value actually differs from the current live
            // value (avoids noisy "Was £3 → Now £3" rows).
            const newPricePence = pe.price_pennies;
            const newPricePounds = (typeof newPricePence === "number")
              ? newPricePence / 100
              : (typeof pe.price === "number" ? pe.price : null);
            // Iteration 95 — guard the "was" price by type instead of
            // truthiness so a null listing_price does not silently
            // render "Was £0.00 → Now £X".
            const hasWasPrice = typeof l.listing_price === "number";
            const priceChanged = hasWasPrice && newPricePounds !== null
              && Math.round(l.listing_price * 100) !== Math.round(newPricePounds * 100);
            const titleChanged = pe.title && pe.title !== l.listing_title;
            const descChanged = pe.description !== undefined
              && pe.description !== null
              && pe.description !== (l.listing_description || "");
            const condChanged = pe.condition && pe.condition !== l.listing_condition;
            const hasDiff = priceChanged || titleChanged || descChanged || condChanged;
            return (
              <div key={l.listing_id} className={`rounded-2xl border ${isFirstApproval ? "border-brandTeal/25 bg-gradient-to-br from-brandTeal/6 via-white to-transparent" : "border-violet-200 bg-violet-50/40"} px-4 py-3 flex flex-wrap items-center gap-3`} data-testid={`pending-listing-${l.listing_id}`}>
                {l.listing_thumbnail && (
                  isFirstApproval ? (
                    <button
                      type="button"
                      onClick={() => openInspectListing(l)}
                      className="flex-shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brandSlate"
                      data-testid={`pending-listing-view-photo-${l.listing_id}`}
                      title="View full details (read-only)"
                    >
                      <img src={l.listing_thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover border border-brandTeal/25" />
                    </button>
                  ) : (
                    <img src={l.listing_thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover border border-violet-200" />
                  )
                )}
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isFirstApproval ? (
                      <span className="pill bg-brandTeal/15 text-brandTeal border border-brandTeal/30 text-[10px]" data-testid={`pending-listing-badge-${l.listing_id}`}>NEW LISTING</span>
                    ) : (
                      <span className="pill bg-violet-100 text-violet-800 border border-violet-200 text-[10px]" data-testid={`pending-listing-badge-${l.listing_id}`}>KID EDIT</span>
                    )}
                    {isFirstApproval && l.ai_title_applied && (
                      <span
                        className="inline-flex items-center gap-1 pill bg-gradient-to-r from-amber-100 to-pink-100 text-amber-900 border border-amber-300 text-[10px] font-bold"
                        data-testid={`pending-listing-ai-title-pill-${l.listing_id}`}
                        title="Our AI helper read the photo and improved the title before your kid submitted it."
                      >
                        <Sparkles className="w-3 h-3 text-amber-600" /> AI-suggested title from photo
                      </span>
                    )}
                    {isFirstApproval ? (
                      <button
                        type="button"
                        onClick={() => openInspectListing(l)}
                        className="font-semibold text-sm text-slate-900 hover:text-brandSlate hover:underline text-left"
                        data-testid={`pending-listing-view-title-${l.listing_id}`}
                        title="View full details (read-only)"
                      >{l.listing_title || "Listing"}</button>
                    ) : (
                      <div className="font-semibold text-sm text-slate-900">{l.listing_title || "Listing"}</div>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    By {kidName} · {priceLabel}
                  </div>
                  {/* Iteration 209 — surface the AI-title diff inline on
                      first-approval cards so parents see the enhancement
                      ("~Book~ → Zero to One by Peter Thiel") at a glance. */}
                  {isFirstApproval && l.ai_title_applied && l.ai_title_original && l.ai_title_original !== l.listing_title && (
                    <div
                      className="mt-2 rounded-xl bg-white border border-amber-200 px-3 py-1.5 text-[11px] leading-relaxed"
                      data-testid={`pending-listing-ai-title-diff-${l.listing_id}`}
                    >
                      <span className="text-slate-400 line-through mr-1">{l.ai_title_original}</span>
                      <span className="text-slate-500 mr-1">→</span>
                      <span className="font-semibold text-amber-900">{l.listing_title}</span>
                    </div>
                  )}
                  {!isFirstApproval && hasDiff && (
                    <div
                      className="mt-2 rounded-xl bg-white border border-violet-200 px-3 py-2 text-[11px] space-y-1"
                      data-testid={`pending-listing-diff-${l.listing_id}`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Proposed changes</div>
                      {titleChanged && (
                        <div className="text-slate-700" data-testid={`pending-listing-diff-title-${l.listing_id}`}>
                          <span className="font-bold text-slate-500">Title:</span>{" "}
                          <span className="line-through text-slate-400">&ldquo;{l.listing_title}&rdquo;</span>{" → "}
                          <span className="font-semibold text-violet-900">&ldquo;{pe.title}&rdquo;</span>
                        </div>
                      )}
                      {priceChanged && (
                        <div className="text-slate-700" data-testid={`pending-listing-diff-price-${l.listing_id}`}>
                          <span className="font-bold text-slate-500">Price:</span>{" "}
                          <span className="line-through text-slate-400">Was {formatGBP(l.listing_price)}</span>{" → "}
                          <span className="font-semibold text-violet-900">Now {formatGBP(newPricePounds)}</span>
                        </div>
                      )}
                      {condChanged && (
                        <div className="text-slate-700" data-testid={`pending-listing-diff-condition-${l.listing_id}`}>
                          <span className="font-bold text-slate-500">Condition:</span>{" "}
                          <span className="line-through text-slate-400">{l.listing_condition || "—"}</span>{" → "}
                          <span className="font-semibold text-violet-900">{pe.condition}</span>
                        </div>
                      )}
                      {descChanged && (
                        <div className="text-slate-700" data-testid={`pending-listing-diff-description-${l.listing_id}`}>
                          <span className="font-bold text-slate-500">Description:</span>{" "}
                          <span className="font-semibold text-violet-900 italic break-words">&ldquo;{(pe.description || "").slice(0, 120)}{(pe.description || "").length > 120 ? "…" : ""}&rdquo;</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {isFirstApproval ? (
                  <div className="flex items-center gap-2 flex-wrap flex-shrink-0 w-full sm:w-auto">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openInspectListing(l)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-brandSlate border border-slate-300 text-[11px] font-bold transition disabled:opacity-60"
                      data-testid={`pending-listing-view-${l.listing_id}`}
                      title="View full details (read-only)"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    {onEditListing && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onEditListing(l.listing_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 text-[11px] font-bold transition disabled:opacity-60"
                        data-testid={`pending-listing-edit-${l.listing_id}`}
                        title="Edit before approving"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    )}
                    {onDeleteListing && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onDeleteListing({
                          id: l.listing_id,
                          title: l.listing_title || "Listing",
                        })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 text-[11px] font-bold transition disabled:opacity-60"
                        data-testid={`pending-listing-delete-${l.listing_id}`}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => rejectListing(l)}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-white border border-rose-200 hover:border-rose-400 text-rose-700 hover:bg-rose-50 text-xs font-bold transition disabled:opacity-60"
                      data-testid={`pending-listing-reject-${l.listing_id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => approveListing(l)}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition disabled:opacity-60"
                      data-testid={`pending-listing-approve-${l.listing_id}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      setBusyId(l.listing_id);
                      try {
                        await api.post(`/parent/listings/${l.listing_id}/reapprove-kid-edit`);
                        showFlash("Re-approved — listing is live again.");
                        await load();
                        onToast?.("Re-approved.");
                      } catch (e) { setErr(formatApiError(e) || "Couldn't re-approve."); }
                      finally { setBusyId(""); }
                    }}
                    className="btn-primary !py-2 !px-4 text-sm"
                    data-testid={`pending-listing-reapprove-${l.listing_id}`}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Re-approve
                  </button>
                )}
              </div>
            );
          })}
          {orders.map((o) => {
            const remaining = timeRemaining(o.expires_at);
            const totalPence = (o.amount_pennies || 0) + (o.fee_pennies || 0);
            const priceLabel = formatGBP((o.price_pennies || o.amount_pennies || 0) / 100);
            const buyerName = o.buyer_name || kidNameFor(o.buyer_kid_id || o.kid_id);
            const sellerName = o.seller_name || "Seller";
            const itemTitle = o.item_title || o.listing_title || "Toy";
            const busy = busyId === o.id;
            // Iteration 75 — bulky detection + parent-side fulfillment gate.
            const isBulky = o.package_size === "bulky";
            const listingId = o.listing_id || o.toy_id;
            const savedChoices = Array.isArray(o.parent_fulfillment_choices) ? o.parent_fulfillment_choices : [];
            const pendingBulkyDecision = isBulky && savedChoices.length === 0;
            const localChoices = bulkyChoices[listingId] || savedChoices;
            const savingBulky = savingBulkyId === listingId;
            return (
              <div key={o.id} className="rounded-2xl border border-brandPink/25 bg-gradient-to-br from-brandPink/6 via-white to-transparent p-4" data-testid={`pending-order-${o.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brandPink/15 text-brandPink flex-shrink-0">
                    <ShoppingBag className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink">Reservation · Kid Purchase</div>
                    <div className="font-bold text-slate-900 leading-tight" data-testid={`pending-order-summary-${o.id}`}>
                      <span data-testid={`pending-order-buyer-${o.id}`}>{buyerName}</span>{" wants to buy "}
                      <span className="italic" data-testid={`pending-order-item-${o.id}`}>
                        <button type="button" onClick={() => openInspectOrder(o)} className="italic hover:text-brandPink hover:underline" title="View full details (read-only)">&ldquo;{itemTitle}&rdquo;</button>
                      </span>{" from "}
                      <span data-testid={`pending-order-seller-${o.id}`}>{sellerName}</span>{" for "}
                      <span data-testid={`pending-order-price-${o.id}`}>{priceLabel}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>Item {formatGBP((o.amount_pennies || 0) / 100)}</span>
                      {o.fee_pennies ? <span>&middot; Fee {formatGBP(o.fee_pennies / 100)}</span> : null}
                      {totalPence !== (o.price_pennies || o.amount_pennies || 0) && (
                        <span>&middot; Total {formatGBP(totalPence / 100)}</span>
                      )}
                      <span>&middot; Sent {relativeTime(o.created_at)}</span>
                      {remaining && (
                        <span className={`inline-flex items-center gap-1 font-bold ${remaining.expired ? "text-rose-700" : "text-amber-700"}`}>
                          <Clock className="w-3 h-3" /> {remaining.label}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Iteration 75 — Bulky-item highlight + parent fulfillment picker */}
                  {isBulky && listingId && (
                    <div className="w-full basis-full mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-3" data-testid={`bulky-panel-${o.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-amber-700" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900" data-testid={`bulky-badge-${o.id}`}>
                          Bulky Item &middot; Parent must pick delivery
                        </span>
                      </div>
                      <p className="text-[12px] text-amber-900 mb-2">
                        Pick one or more fulfillment options the buyer can choose from. Local options route contact through Parent-to-Parent chat only; addresses are hidden until payment completes.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2" data-testid={`bulky-choices-${o.id}`}>
                        {BULKY_FULFILLMENT_OPTS.map((f) => {
                          const checked = localChoices.includes(f.value);
                          return (
                            <label
                              key={f.value}
                              className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl border cursor-pointer transition-all ${
                                checked ? "border-amber-500 bg-white text-amber-900" : "border-amber-200 bg-white/60 text-amber-800 hover:border-amber-400"
                              }`}
                              data-testid={`bulky-choice-${f.value}-${o.id}`}
                            >
                              <input
                                type="checkbox"
                                className="accent-amber-600"
                                checked={checked}
                                onChange={() => toggleBulkyChoice(listingId, f.value)}
                                disabled={savedChoices.length > 0 && !bulkyChoices[listingId]}
                              />
                              {f.label}
                            </label>
                          );
                        })}
                      </div>
                      {savedChoices.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => saveBulkyChoices(listingId)}
                          disabled={savingBulky || (localChoices || []).length === 0}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition disabled:opacity-60"
                          data-testid={`bulky-save-${o.id}`}
                        >
                          {savingBulky ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save fulfillment options
                        </button>
                      ) : (
                        <div className="text-[11px] font-semibold text-emerald-700" data-testid={`bulky-saved-${o.id}`}>
                          <Check className="inline w-3 h-3 mr-1" /> Fulfillment saved: {savedChoices.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openInspectOrder(o)}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-300 hover:border-brandSlate text-brandSlate hover:bg-slate-50 text-xs font-bold transition disabled:opacity-60"
                      data-testid={`pending-order-view-${o.id}`}
                      title="View full details (read-only)"
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <button
                      type="button"
                      onClick={() => decide("order", o.id, false)}
                      disabled={busy}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-white border border-rose-200 hover:border-rose-400 text-rose-700 hover:bg-rose-50 text-xs font-bold transition disabled:opacity-60"
                      data-testid={`pending-order-reject-${o.id}`}
                      title="Release the reservation and put the item back on the marketplace"
                    >
                      <X className="w-3.5 h-3.5" /> Release / Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => decide("order", o.id, true)}
                      disabled={busy || pendingBulkyDecision}
                      title={pendingBulkyDecision ? "Save fulfillment options first (bulky item)" : undefined}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition disabled:opacity-60"
                      data-testid={`pending-order-approve-${o.id}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Review &amp; Approve
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {requests.map((r) => {
            const busy = busyId === r.id;
            return (
              <div key={r.id} className="rounded-2xl border border-brandTeal/25 bg-gradient-to-br from-brandTeal/6 via-white to-transparent p-4" data-testid={`pending-allowance-${r.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brandTeal/15 text-brandTeal flex-shrink-0">
                    <PiggyBank className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandTeal">Allowance Request</div>
                    <div className="font-bold text-slate-900 leading-tight truncate">
                      {kidNameFor(r.kid_id)} asked for {formatGBP((r.amount_pennies || 0) / 100)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">Asked {relativeTime(r.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => decide("request", r.id, false)}
                      disabled={busy}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-400 text-slate-700 hover:bg-slate-50 text-xs font-bold transition disabled:opacity-60"
                      data-testid={`pending-allowance-reject-${r.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Dismiss
                    </button>
                    <button
                      type="button"
                      onClick={() => decide("request", r.id, true)}
                      disabled={busy}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold shadow-sm transition disabled:opacity-60"
                      data-testid={`pending-allowance-approve-${r.id}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Acknowledge
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Iteration 259 — view-only inspection modal (4 event variants). */}
      {inspect && (
        <InspectListingModal
          variant={inspect.variant}
          details={inspect.details}
          busy={busyId === inspect.id}
          onClose={() => setInspect(null)}
          onApprove={inspect.kind === "listing"
            ? () => approveListing(inspect.payload)
            : () => decide("order", inspect.id, true)}
          onReject={inspect.kind === "listing"
            ? () => rejectListing(inspect.payload)
            : () => decide("order", inspect.id, false)}
          onEdit={inspect.kind === "listing" && onEditListing
            ? () => { const id = inspect.id; setInspect(null); onEditListing(id); }
            : null}
          onDelete={inspect.kind === "listing" && onDeleteListing
            ? () => { const l = inspect.payload; setInspect(null); onDeleteListing({ id: l.listing_id, title: l.listing_title || "Listing" }); }
            : null}
        />
      )}
    </div>
  );
}
